import { ClientOnly, type LinkProps } from '@tanstack/react-router';

import { useAuthSession } from '@/services/auth-service';
import * as m from '@/paraglide/messages';
import { localizeHref } from '@/paraglide/runtime';

/**
 * Marketing nav auth slot.
 *
 * Marketing routes are SSR'd and CDN-shared (`cacheHeadersFor`, s-maxage=300),
 * so the rendered HTML must stay user-independent. The login link is that
 * user-independent state: it's baked into the server HTML (every visitor gets
 * the same bytes, so the CDN cache key is untouched) and stays up through
 * hydration and the pending session read. Only a resolved signed-in session
 * swaps in the Dashboard chip, client-side. `ClientOnly` keeps `useSession`
 * from firing during SSR; its fallback is the same login link, so server and
 * first client render match.
 *
 * The slot is a 1x1 grid holding an invisible width reserver stacked under the
 * live state, so the box is already its final size during SSR and neither the
 * signed-out nor the signed-in resolve moves anything. See the reserver for why
 * this measures rather than hardcodes.
 */
export function AuthNavLink({
  align = 'start',
  anonymous = 'login',
  className,
  role,
}: {
  /**
   * Which edge the resolved state hugs inside the reserved box. The desktop nav
   * is a right-anchored row, so `end` keeps the link flush against the CTA
   * exactly where it sits today; the mobile menu is a left-aligned column.
   */
  align?: 'start' | 'end';
  /**
   * What an anonymous (or still-resolving) visitor sees. The squeeze page uses
   * `none`: a login link there is an exit from the funnel, and with nothing to
   * swap in there is no width to reserve either.
   */
  anonymous?: 'login' | 'none';
  className: string;
  role?: string;
}) {
  const anonymousSlot =
    anonymous === 'login' ? (
      <LoginLink className={className} role={role} />
    ) : null;
  return (
    // role='none' keeps this layout wrapper out of the a11y tree, so the mobile
    // menu still directly owns its menuitem.
    <span
      className={`grid shrink-0 [&>*]:col-start-1 [&>*]:row-start-1 ${
        align === 'end' ? 'justify-items-end' : 'justify-items-start'
      }`}
      role='none'
    >
      {anonymous === 'login' && <AuthNavWidthReserver className={className} />}
      <ClientOnly fallback={anonymousSlot}>
        <ResolvedAuthNavLink
          anonymousSlot={anonymousSlot}
          className={className}
          role={role}
        />
      </ClientOnly>
    </span>
  );
}

/**
 * Where the marketing nav's auth slot points. Signed-in visitors go straight to
 * the app instead of being shown a login link they don't need.
 */
export function authNavTarget(isSignedIn: boolean): {
  href: NonNullable<LinkProps['to']>;
  label: string;
} {
  return isSignedIn
    ? { href: '/app/home', label: m.nav_dashboard() }
    : { href: '/app/login', label: m.common_login() };
}

/**
 * Matches the app header's avatar (`useNameAndEmail` in user-service), so the
 * same visitor sees the same two letters in the marketing nav and in the app.
 *
 * Email rather than `user.name` on purpose: `signUpEmail` stores the email
 * address as the better-auth `name`, so for email signups the two are the same
 * string anyway, and OAuth display names aren't worth a second identity source
 * here.
 */
export function initialsFromEmail(email: string | null): string {
  return email ? email.slice(0, 2).toUpperCase() : 'LI';
}

/**
 * Holds the slot open at its widest state so nothing reflows when auth
 * resolves.
 *
 * The signed-in variant is the wider of the two and — crucially — has no
 * user-dependent width: a fixed `size-7` disc (always two initials) plus a
 * static label. So the widest state is knowable at SSR time. Rendering the real
 * markup invisibly measures it in the browser's own layout, which keeps this
 * correct across locales ("Dashboard" vs "Panel") and breakpoints without a
 * hardcoded `min-w` to drift.
 *
 * Anonymous visitors don't pay for this: `justify-items-end` puts the slack on
 * the left, where the nav already had whitespace, so "Login" sits exactly where
 * it always did.
 */
function AuthNavWidthReserver({ className }: { className: string }) {
  return (
    <span
      aria-hidden
      className={`${className} invisible flex items-center gap-2`}
    >
      <span className='size-7 shrink-0 rounded-full' />
      {m.nav_dashboard()}
    </span>
  );
}

/**
 * Anonymous markup, byte-identical to what the nav rendered before this slot
 * existed, so the indexed SiteNavigationElement is unchanged. Doubles as the
 * SSR fallback and the pending state: swapping between two renders of it is
 * visually a no-op.
 */
function LoginLink({
  className,
  role,
}: {
  className: string;
  role?: string | undefined;
}) {
  const { href, label } = authNavTarget(false);
  return (
    <a
      className={className}
      href={localizeHref(href)}
      itemProp='url'
      role={role}
    >
      <span itemProp='name'>{label}</span>
    </a>
  );
}

function ResolvedAuthNavLink({
  anonymousSlot,
  className,
  role,
}: {
  anonymousSlot: React.ReactNode;
  className: string;
  role?: string | undefined;
}) {
  const { user, isPending } = useAuthSession();

  // Login stays up while the session resolves — it's what the server HTML
  // already shows, and blanking it here is what made the link pop in late.
  if (isPending || !user) return anonymousSlot;

  const { href, label } = authNavTarget(true);

  // No schema.org microdata on this branch: SiteNavigationElement describes the
  // site's navigation, not the visitor. The email rides in the accessible name
  // rather than in DOM text, so no PII lands in the markup.
  return (
    <a
      aria-label={m.nav_dashboard_signed_in_as({ email: user.email })}
      className={`${className} flex items-center gap-2`}
      href={localizeHref(href)}
      role={role}
    >
      {/*
        Deliberately not the shared radix `Avatar`: its whole job is image
        load-state management, and there is no image source here. A plain disc
        keeps @radix-ui/react-avatar out of the marketing bundle.
      */}
      <span className='flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-neutral-200 text-xs font-medium text-black'>
        {initialsFromEmail(user.email)}
      </span>
      <span aria-hidden>{label}</span>
    </a>
  );
}
